import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { RequireOrg, VpState } from '../components/VelopulseShell';
import useLoad from '../utils/useLoad';
import { vpApi, errorMessage, isNotFound, fmtMoney, fmtNum, fmtDateTime, fmtWan, stageOf, VEHICLE_STATUS, vehicleTitle } from '../api/velopulse';

// 車輛庫存固定欄位組（R1）；自訂欄位組態屬 R3
const FIELDS = [
  { key: 'brand', label: '廠牌' },
  { key: 'model', label: '車款' },
  { key: 'model_year', label: '年式', type: 'number' },
  { key: 'exterior_color', label: '顏色' },
  { key: 'mileage', label: '里程（公里）', type: 'number', fmt: fmtNum },
  { key: 'asking_price', label: '標價', type: 'number', fmt: fmtMoney },
  { key: 'plate_no', label: '車牌' },
  { key: 'vin', label: '車身號碼' },
  { key: 'status', label: '狀態', type: 'status', fmt: (v) => VEHICLE_STATUS[v]?.label || '—' },
  { key: 'note', label: '內部備註', type: 'textarea', wide: true },
];

function RecordDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: rec, error, loading, reload, setData } = useLoad(() => vpApi.getRecord(id), [id]);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  if (loading) return <VpState loading />;
  if (error) {
    const notFound = isNotFound(error);
    return (
      <div className="vp-content">
        <div className="vp-card">
          <VpState
            text={notFound ? '找不到這筆紀錄，或它不屬於目前的組織。' : errorMessage(error, '紀錄載入失敗。')}
            action={{ label: '回到紀錄庫', onClick: () => navigate('/records') }}
          />
        </div>
      </div>
    );
  }

  const st = VEHICLE_STATUS[rec.status];
  const startEdit = () => { setSaveError(''); setEdit(Object.fromEntries(FIELDS.map((f) => [f.key, rec[f.key] ?? '']))); };

  const save = async () => {
    const patch = {};
    FIELDS.forEach((f) => {
      let v = edit[f.key];
      if (f.type === 'number') v = v === '' ? null : Number(v);
      if ((rec[f.key] ?? (f.type === 'number' ? null : '')) !== v) patch[f.key] = v;
    });
    if (!Object.keys(patch).length) { setEdit(null); return; }
    setSaving(true); setSaveError('');
    try {
      const updated = await vpApi.updateRecord(id, patch);
      setData((d) => ({ ...d, ...updated }));
      setEdit(null);
      toast.success('已儲存變更');
      reload();
    } catch (err) {
      setSaveError(errorMessage(err, '儲存失敗，請稍後再試。'));
    } finally { setSaving(false); }
  };

  const renderInput = (f) => {
    const common = { id: `rf-${f.key}`, className: 'vp-input', value: edit[f.key], onChange: (e) => setEdit((s) => ({ ...s, [f.key]: e.target.value })) };
    if (f.type === 'status') return <select {...common}>{Object.entries(VEHICLE_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select>;
    if (f.type === 'textarea') return <textarea {...common} />;
    return <input {...common} type={f.type === 'number' ? 'number' : 'text'} />;
  };

  return (
    <div className="vp-content">
      <Link className="vp-back" to="/records">← 回到紀錄庫</Link>
      <div className="vp-detail-head" style={{ marginTop: 8 }}>
        <h1>{rec.code} · {vehicleTitle(rec)}</h1>
        {st && <span className={`vp-badge ${st.tone}`}>{st.label}</span>}
        <div className="vp-spacer" />
        {!edit && <button type="button" className="vp-btn primary" onClick={startEdit}>編輯</button>}
      </div>

      <div className="vp-detail-grid">
        <div className="vp-card">
          <h2>車輛資料 <span className="vp-muted" style={{ fontSize: 12, fontWeight: 400 }}>類型：車輛庫存 · 更新於 {fmtDateTime(rec.updated_at) || '—'}</span></h2>
          <div className="vp-fields">
            {FIELDS.map((f) => (
              <div className="vp-kv" key={f.key} style={f.wide ? { gridColumn: '1 / -1' } : undefined}>
                {edit
                  ? <><label className="k" htmlFor={`rf-${f.key}`}>{f.label}</label><div className="v">{renderInput(f)}</div></>
                  : <><div className="k">{f.label}</div><div className="v">{(f.fmt ? f.fmt(rec[f.key]) : rec[f.key]) || '—'}</div></>}
              </div>
            ))}
          </div>
          {saveError && <div className="vp-error" role="alert">{saveError}</div>}
          {edit && (
            <div className="vp-wiz-actions">
              <button type="button" className="vp-btn" onClick={() => setEdit(null)} disabled={saving}>取消</button>
              <button type="button" className="vp-btn primary" onClick={save} disabled={saving}>{saving ? '儲存中…' : '儲存變更'}</button>
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gap: 16 }}>
          <div className="vp-card">
            <h2>關聯商機</h2>
            {!(rec.opportunities || []).length && <VpState text="這輛車目前沒有關聯的商機。" action={{ label: '新增商機', onClick: () => navigate(`/opportunities?new=1&record=${rec.id}`) }} />}
            {(rec.opportunities || []).map((o) => {
              const s = stageOf(o.stage);
              return (
                <Link className="vp-item" key={o.id} to={`/opportunities/${o.id}`}>
                  <span className={`vp-badge ${s.tone}`}>{s.label}</span>
                  <div><strong>{o.title}</strong><div className="vp-sub">{o.amount != null ? `預估 ${fmtWan(o.amount)}` : o.summary}</div></div>
                </Link>
              );
            })}
          </div>
          <div className="vp-card">
            <h2>活動時間軸</h2>
            {!(rec.activities || []).length && <VpState text="這筆紀錄還沒有任何活動。" />}
            <ul className="vp-timeline">
              {(rec.activities || []).map((a) => (
                <li key={a.id}><div>{a.text}</div><div className="vp-muted" style={{ fontSize: 12 }}>{[a.actor, fmtDateTime(a.at)].filter(Boolean).join(' · ')}</div></li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RecordDetailPage() {
  return <RequireOrg><RecordDetail /></RequireOrg>;
}
